import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Search, Download, Printer, Copy, X, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, RefreshCw, Plus, Calendar,
  Edit, Save, List, LayoutGrid, ChevronDown, ChevronUp, Undo2, Barcode,
} from "lucide-react";
import { MobileFilterToggle, MobileFilterClose, sidebarClasses } from "@/components/PageWithSidebar";

const STATUS_OPTIONS = [
  { key: "draft", label: "Phiếu tạm" },
  { key: "imported", label: "Đã nhập hàng" },
  { key: "cancelled", label: "Đã hủy" },
];

const TIME_OPTIONS = [
  { key: "today", label: "Hôm nay" },
  { key: "yesterday", label: "Hôm qua" },
  { key: "this_week", label: "Tuần này" },
  { key: "this_month", label: "Tháng này" },
  { key: "30days", label: "30 ngày qua" },
  { key: "90days", label: "90 ngày qua" },
  { key: "365days", label: "1 năm qua" },
  { key: "all", label: "Tất cả thời gian" },
  { key: "custom", label: "Lựa chọn khác" },
];

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      if (dateStr.includes("T")) {
        const parts = dateStr.split("T");
        const datePart = parts[0].split("-");
        const timePart = (parts[1] || "").split(":").slice(0, 2).join(":");
        return `${datePart[2]}/${datePart[1]}/${datePart[0]} ${timePart}`;
      }
      return dateStr;
    }
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const mins = String(d.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${mins}`;
  } catch {
    return dateStr;
  }
}

export default function PurchaseOrdersPage() {
  // Filter states
  const [searchCode, setSearchCode] = useState("");
  const [searchProduct, setSearchProduct] = useState("");
  const [searchSupplier, setSearchSupplier] = useState("");
  const [expandedSearch, setExpandedSearch] = useState(false);
  const [timeFilter, setTimeFilter] = useState("30days");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilters, setStatusFilters] = useState(["draft", "imported"]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // UI states
  const [showFilter, setShowFilter] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);

  // Build query params
  const queryParams = useMemo(() => ({
    search: searchCode || undefined,
    search_product: searchProduct || undefined,
    search_supplier: searchSupplier || undefined,
    time_filter: timeFilter,
    date_from: timeFilter === "custom" ? dateFrom : undefined,
    date_to: timeFilter === "custom" ? dateTo : undefined,
    status: statusFilters.length > 0 ? statusFilters.join(",") : undefined,
    page,
    page_size: pageSize,
  }), [searchCode, searchProduct, searchSupplier, timeFilter, dateFrom, dateTo, statusFilters, page, pageSize]);

  // Fetch purchase orders
  const { data: poData, isLoading, refetch } = useQuery({
    queryKey: ["purchase-orders", queryParams],
    queryFn: () => api.get("/purchase-orders", { params: queryParams }).then(r => r.data),
    keepPreviousData: true,
  });

  const purchaseOrders = poData?.purchase_orders || [];
  const totalCount = poData?.total || 0;
  const summary = poData?.summary || {};
  const totalPages = Math.ceil(totalCount / pageSize);

  // Toggle status filter
  const toggleStatus = (key) => {
    setStatusFilters(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
    setPage(1);
  };

  // Sync from KiotViet
  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/purchase-orders/sync", null, { params: { days: 30 } });
      toast.success(res.data.message || "Đồng bộ thành công");
      refetch();
    } catch (err) {
      toast.error("Lỗi đồng bộ: " + (err.response?.data?.detail || err.message));
    } finally {
      setSyncing(false);
    }
  };

  // Cancel
  const handleCancel = async (id) => {
    try {
      await api.post(`/purchase-orders/${id}/cancel`);
      toast.success("Đã hủy phiếu nhập hàng");
      refetch();
      setCancelDialog(null);
      setExpandedRow(null);
    } catch (err) {
      toast.error("Lỗi: " + (err.response?.data?.detail || err.message));
    }
  };

  // Copy
  const handleCopy = async (id) => {
    try {
      const res = await api.post(`/purchase-orders/${id}/copy`);
      toast.success(res.data.message || "Đã sao chép");
      refetch();
    } catch (err) {
      toast.error("Lỗi: " + (err.response?.data?.detail || err.message));
    }
  };

  // Return
  const handleReturn = async (id) => {
    try {
      const res = await api.post(`/purchase-orders/${id}/return`);
      toast.success(res.data.message || "Đã tạo phiếu trả hàng");
      refetch();
    } catch (err) {
      toast.error("Lỗi: " + (err.response?.data?.detail || err.message));
    }
  };

  // Print
  const handlePrint = (po) => {
    const printContent = `
      <html><head><title>Phiếu nhập hàng ${po.code}</title>
      <style>body{font-family:sans-serif;padding:20px;max-width:600px;margin:0 auto}
      table{width:100%;border-collapse:collapse}td,th{padding:6px;text-align:left;border:1px solid #ddd}
      .right{text-align:right}.center{text-align:center}.bold{font-weight:bold}
      h2{text-align:center;margin-bottom:5px}p{margin:3px 0}</style></head>
      <body><h2>KARINOX Arena</h2><p class="center">PHIẾU NHẬP HÀNG</p><hr/>
      <p><b>Mã phiếu:</b> ${po.code}</p>
      <p><b>Ngày:</b> ${formatDateTime(po.purchase_date)}</p>
      <p><b>Nhà cung cấp:</b> ${po.supplier_name || "-"}</p>
      <p><b>Người nhập:</b> ${po.importer_name || "-"}</p>
      <p><b>Chi nhánh:</b> ${po.branch_name}</p><hr/>
      <table><tr><th>Mã hàng</th><th>Tên hàng</th><th class="right">SL</th><th class="right">Đơn giá</th><th class="right">Thành tiền</th></tr>
      ${(po.details || []).map(d => `<tr><td>${d.product_code}</td><td>${d.product_name}</td><td class="right">${d.quantity}</td><td class="right">${formatVND(d.import_price)}</td><td class="right">${formatVND(d.total)}</td></tr>`).join("")}
      </table><hr/>
      <p class="bold">Tổng số lượng: ${po.total_quantity}</p>
      <p class="bold">Tổng tiền hàng: ${formatVND(po.total_amount)}</p>
      <p class="bold">Tổng cộng: ${formatVND(po.grand_total)}</p>
      </body></html>`;
    const win = window.open("", "_blank");
    win.document.write(printContent);
    win.document.close();
    win.print();
  };

  // Print Barcode
  const handlePrintBarcode = (po) => {
    const printContent = `
      <html><head><title>Mã vạch - ${po.code}</title>
      <style>body{font-family:monospace;padding:10px}
      .item{display:inline-block;border:1px solid #ccc;padding:8px;margin:4px;text-align:center;width:200px}
      .barcode{font-size:24px;letter-spacing:2px;font-family:'Libre Barcode 39',monospace}
      .name{font-size:10px;margin-top:4px}</style>
      <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+39&display=swap" rel="stylesheet">
      </head><body>
      ${(po.details || []).map(d => `<div class="item"><div class="barcode">*${d.product_code}*</div><div class="name">${d.product_name}</div><div>${d.product_code} - SL: ${d.quantity}</div></div>`).join("")}
      </body></html>`;
    const win = window.open("", "_blank");
    win.document.write(printContent);
    win.document.close();
    win.print();
  };

  // Export CSV
  const handleExport = () => {
    const headers = ["Mã nhập hàng", "Thời gian", "Nhà cung cấp", "Tổng tiền", "Cần trả NCC", "Trạng thái"];
    const rows = purchaseOrders.map(po => [
      po.code, formatDateTime(po.purchase_date), po.supplier_name,
      po.grand_total, po.amount_owed, po.status_label,
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `phieu_nhap_hang_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Đã xuất file CSV");
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  return (
    <div className="flex h-full bg-white dark:bg-slate-900" data-testid="purchase-orders-page">
      {/* Left Sidebar Filters */}
      <aside className={sidebarClasses(showFilter)} data-testid="po-filter-panel">
        <MobileFilterClose setShowFilter={setShowFilter} />
        {/* Search */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Tìm kiếm
            <ChevronUp className="w-4 h-4" />
          </h4>
          <Input
            placeholder="Theo mã phiếu nhập"
            value={searchCode}
            onChange={e => { setSearchCode(e.target.value); setPage(1); }}
            className="mb-2 text-sm"
            data-testid="search-po-code"
          />
          <Input
            placeholder="Theo mã, tên hàng"
            value={searchProduct}
            onChange={e => { setSearchProduct(e.target.value); setPage(1); }}
            className="mb-2 text-sm"
            data-testid="search-po-product"
          />
          <Input
            placeholder="Theo mã, tên NCC"
            value={searchSupplier}
            onChange={e => { setSearchSupplier(e.target.value); setPage(1); }}
            className="mb-2 text-sm"
            data-testid="search-po-supplier"
          />
          <button
            onClick={() => setExpandedSearch(!expandedSearch)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
          >
            {expandedSearch ? "Thu gọn" : "Mở rộng"} {expandedSearch ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {/* Time Filter */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Thời gian
            <ChevronUp className="w-4 h-4" />
          </h4>
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="time" checked={timeFilter !== "custom"} onChange={() => setTimeFilter("this_month")} className="accent-blue-600" />
              <select
                value={timeFilter === "custom" ? "this_month" : timeFilter}
                onChange={e => { setTimeFilter(e.target.value); setPage(1); }}
                className="text-sm border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200 flex-1"
                data-testid="po-time-filter"
              >
                {TIME_OPTIONS.filter(t => t.key !== "custom").map(t => (
                  <option key={t.key} value={t.key} className="bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200">{t.label}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="time" checked={timeFilter === "custom"} onChange={() => setTimeFilter("custom")} className="accent-blue-600" />
              <span className="text-sm text-gray-600 dark:text-slate-400">Lựa chọn khác</span>
              <Calendar className="w-4 h-4 text-gray-400" />
            </label>
            {timeFilter === "custom" && (
              <div className="pl-6 space-y-1">
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="text-xs" />
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="text-xs" />
              </div>
            )}
          </div>
        </div>

        {/* Status */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Trạng thái
            <ChevronUp className="w-4 h-4" />
          </h4>
          <div className="space-y-1.5">
            {STATUS_OPTIONS.map(opt => (
              <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={statusFilters.includes(opt.key)}
                  onCheckedChange={() => toggleStatus(opt.key)}
                  data-testid={`po-status-${opt.key}`}
                />
                <span className="text-sm text-gray-600 dark:text-slate-400">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Page Size */}
        <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
          <label className="text-sm text-gray-600 dark:text-slate-400 flex items-center gap-2">
            Số bản ghi:
            <select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200 text-sm"
              data-testid="po-page-size"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 gap-2 border-b border-gray-200 dark:border-slate-700">
          <h1 className="text-xl font-bold text-gray-800 dark:text-slate-100" data-testid="po-title">
            Phiếu nhập hàng
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => setCreateDialog(true)} className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1" data-testid="btn-new-po">
              <Plus className="w-4 h-4" /> Nhập hàng
            </Button>
            <Button onClick={handleSync} variant="outline" className="text-sm gap-1" disabled={syncing} data-testid="btn-sync-po">
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Đang đồng bộ..." : "Đồng bộ KiotViet"}
            </Button>
            <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1" data-testid="btn-export-po">
              <Download className="w-4 h-4" /> Xuất file
            </Button>
            <div className="flex border border-gray-300 dark:border-slate-600 rounded overflow-hidden">
              <button className="p-1.5 bg-blue-600 text-white"><List className="w-4 h-4" /></button>
              <button className="p-1.5 bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300"><LayoutGrid className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm" data-testid="po-table">
            <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-slate-700">
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Mã nhập hàng</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Thời gian</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Nhà cung cấp</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300">Cần trả NCC</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Trạng thái</th>
              </tr>
              {/* Summary */}
              <tr className="border-b border-gray-200 dark:border-slate-600 bg-blue-50/50 dark:bg-blue-900/20">
                <td colSpan={3} className="px-3 py-2"></td>
                <td className="px-3 py-2 text-right font-bold text-blue-700 dark:text-blue-300">{formatVND(summary.total_owed || 0)}</td>
                <td className="px-3 py-2"></td>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={5} className="text-center py-10 text-gray-400">Đang tải...</td></tr>
              ) : purchaseOrders.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-gray-400">Không có phiếu nhập hàng nào</td></tr>
              ) : (
                purchaseOrders.map(po => (
                  <React.Fragment key={po.id}>
                    <tr
                      className={`border-b border-gray-100 dark:border-slate-700 cursor-pointer transition-colors ${
                        expandedRow === po.id ? "bg-blue-50 dark:bg-blue-900/30 font-medium" : "hover:bg-gray-50 dark:hover:bg-slate-800/50"
                      }`}
                      onClick={() => toggleRow(po.id)}
                      data-testid={`po-row-${po.code}`}
                    >
                      <td className="px-3 py-2.5 text-gray-800 dark:text-slate-200">{po.code}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{formatDateTime(po.purchase_date)}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{po.supplier_name || ""}</td>
                      <td className="px-3 py-2.5 text-right text-gray-800 dark:text-slate-200">{formatVND(po.amount_owed)}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{po.status_label}</td>
                    </tr>
                    {expandedRow === po.id && (
                      <tr className="bg-blue-50/30 dark:bg-blue-900/10">
                        <td colSpan={5} className="p-0">
                          <PODetail
                            po={po}
                            onPrint={() => handlePrint(po)}
                            onPrintBarcode={() => handlePrintBarcode(po)}
                            onCopy={() => handleCopy(po.id)}
                            onReturn={() => handleReturn(po.id)}
                            onCancel={() => setCancelDialog(po)}
                            onExport={handleExport}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-4 sm:px-6 py-2 sm:py-3 gap-2 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(1)} disabled={page <= 1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronsLeft className="w-4 h-4" /></button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (page <= 3) pageNum = i + 1;
              else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = page - 2 + i;
              return (
                <button key={pageNum} onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 rounded text-sm ${page === pageNum ? "bg-blue-600 text-white" : "hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-400"}`}>
                  {pageNum}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronsRight className="w-4 h-4" /></button>
          </div>
          <span className="text-sm text-gray-500 dark:text-slate-400" data-testid="po-pagination-info">
            Hiển thị {totalCount > 0 ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, totalCount)} trên tổng số {totalCount} phiếu nhập hàng
          </span>
        </div>
      </main>

      <MobileFilterToggle showFilter={showFilter} setShowFilter={setShowFilter} />

      {/* Cancel Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Xác nhận hủy phiếu nhập hàng</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600 dark:text-slate-400">
            Bạn có chắc muốn hủy phiếu <strong>{cancelDialog?.code}</strong>? Thao tác này không thể hoàn tác.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelDialog(null)}>Đóng</Button>
            <Button variant="destructive" onClick={() => handleCancel(cancelDialog?.id)} data-testid="confirm-cancel-po">Hủy phiếu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create PO Dialog */}
      <CreatePODialog open={createDialog} onClose={() => setCreateDialog(false)} onSuccess={() => { setCreateDialog(false); refetch(); }} />
    </div>
  );
}

// --- PO Detail Component ---
function PODetail({ po, onPrint, onPrintBarcode, onCopy, onReturn, onCancel, onExport }) {
  return (
    <div className="px-6 py-4 border-t border-blue-100 dark:border-blue-800" data-testid="po-detail-panel">
      {/* Tab */}
      <div className="flex gap-4 border-b border-gray-200 dark:border-slate-700 mb-4">
        <button className="pb-2 px-1 text-sm font-medium border-b-2 border-blue-600 text-blue-600">Thông tin</button>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 gap-x-12 gap-y-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Mã phiếu nhập:</span>
          <span className="text-sm font-medium text-gray-800 dark:text-slate-200">{po.code}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Trạng thái:</span>
          <span className={`text-sm font-medium ${po.status === "imported" ? "text-green-600" : po.status === "cancelled" ? "text-red-600" : "text-yellow-600"}`}>
            {po.status_label}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Thời gian:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{formatDateTime(po.purchase_date)}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Chi nhánh:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{po.branch_name}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Nhà cung cấp:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{po.supplier_name || "-"}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Người nhập:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{po.importer_name || "-"}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Người tạo:</span>
          <span className="text-sm text-gray-700 dark:text-slate-300">{po.creator_name || "-"}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Ghi chú:</span>
          <span className="text-sm text-gray-500 dark:text-slate-400 italic">{po.note || "Không có"}</span>
        </div>
      </div>

      {/* Products Table */}
      <div className="border border-gray-200 dark:border-slate-700 rounded overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-slate-700">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-300">Mã hàng hóa</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-slate-300">Tên hàng</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Số lượng</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Đơn giá</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Giảm giá</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Giá nhập</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-slate-300">Thành tiền</th>
            </tr>
          </thead>
          <tbody>
            {(po.details || []).map((d, idx) => (
              <tr key={idx} className="border-t border-gray-100 dark:border-slate-700">
                <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{d.product_code}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{d.product_name}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-slate-300">{d.quantity}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-slate-300">{formatVND(d.price)}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-slate-300">{formatVND(d.discount || 0)}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-slate-300">{formatVND(d.import_price)}</td>
                <td className="px-3 py-2 text-right font-medium text-gray-800 dark:text-slate-200">{formatVND(d.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="flex justify-end mb-4">
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-8"><span className="text-gray-500 dark:text-slate-400">Tổng số lượng:</span><span className="font-medium">{po.total_quantity}</span></div>
          <div className="flex justify-between gap-8"><span className="text-gray-500 dark:text-slate-400">Tổng số mặt hàng:</span><span className="font-medium">{po.total_items}</span></div>
          <div className="flex justify-between gap-8"><span className="text-gray-500 dark:text-slate-400">Tổng tiền hàng:</span><span className="font-medium">{formatVND(po.total_amount)}</span></div>
          <div className="flex justify-between gap-8"><span className="text-gray-500 dark:text-slate-400">Giảm giá phiếu nhập:</span><span>{formatVND(po.discount_amount)}</span></div>
          <div className="flex justify-between gap-8"><span className="text-gray-500 dark:text-slate-400">Tổng cộng:</span><span className="font-medium">{formatVND(po.grand_total)}</span></div>
          <div className="flex justify-between gap-8 pt-1 border-t border-gray-200 dark:border-slate-700">
            <span className="font-semibold">Tiền đã trả NCC:</span>
            <span className="font-bold text-blue-700 dark:text-blue-300">{formatVND(po.paid_to_supplier)}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-2 pt-3 border-t border-gray-200 dark:border-slate-700">
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1" data-testid="btn-save-po"><Save className="w-3 h-3" /> Lưu</Button>
        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs gap-1" onClick={onReturn} data-testid="btn-return-po"><Undo2 className="w-3 h-3" /> Trả hàng</Button>
        <Button size="sm" variant="outline" className="text-xs gap-1" onClick={onPrint} data-testid="btn-print-po"><Printer className="w-3 h-3" /> In</Button>
        <Button size="sm" variant="outline" className="text-xs gap-1" onClick={onPrintBarcode} data-testid="btn-barcode-po"><Barcode className="w-3 h-3" /> In mã vạch</Button>
        <Button size="sm" variant="outline" className="text-xs gap-1" onClick={onExport} data-testid="btn-export-po-detail"><Download className="w-3 h-3" /> Xuất file</Button>
        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs gap-1" onClick={onCopy} data-testid="btn-copy-po"><Copy className="w-3 h-3" /> Sao chép</Button>
        <Button size="sm" variant="destructive" className="text-xs gap-1" onClick={onCancel} data-testid="btn-cancel-po"><X className="w-3 h-3" /> Hủy bỏ</Button>
      </div>
    </div>
  );
}

// --- Create PO Dialog ---
function CreatePODialog({ open, onClose, onSuccess }) {
  const [supplierName, setSupplierName] = useState("");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([{ product_code: "", product_name: "", quantity: 1, price: 0, discount: 0, import_price: 0 }]);
  const [submitting, setSubmitting] = useState(false);

  const addItem = () => setItems([...items, { product_code: "", product_name: "", quantity: 1, price: 0, discount: 0, import_price: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    setItems(newItems);
  };

  const handleSubmit = async () => {
    if (items.every(i => !i.product_name)) {
      toast.error("Vui lòng thêm ít nhất 1 sản phẩm");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/purchase-orders", {
        supplier_name: supplierName,
        note,
        items: items.filter(i => i.product_name),
      });
      toast.success(res.data.message || "Đã tạo phiếu nhập hàng");
      setSupplierName("");
      setNote("");
      setItems([{ product_code: "", product_name: "", quantity: 1, price: 0, discount: 0, import_price: 0 }]);
      onSuccess();
    } catch (err) {
      toast.error("Lỗi: " + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Tạo phiếu nhập hàng mới</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Nhà cung cấp</label>
              <Input placeholder="Tên nhà cung cấp" value={supplierName} onChange={e => setSupplierName(e.target.value)} data-testid="create-po-supplier" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Ghi chú</label>
              <Input placeholder="Ghi chú..." value={note} onChange={e => setNote(e.target.value)} data-testid="create-po-note" />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Danh sách hàng hóa</label>
              <Button size="sm" variant="outline" onClick={addItem} className="text-xs gap-1"><Plus className="w-3 h-3" /> Thêm</Button>
            </div>
            <div className="border border-gray-200 dark:border-slate-700 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 dark:bg-slate-700">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-xs">Mã hàng</th>
                    <th className="px-2 py-1.5 text-left text-xs">Tên hàng</th>
                    <th className="px-2 py-1.5 text-right text-xs">SL</th>
                    <th className="px-2 py-1.5 text-right text-xs">Đơn giá</th>
                    <th className="px-2 py-1.5 text-right text-xs">Giá nhập</th>
                    <th className="px-2 py-1.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx} className="border-t border-gray-100 dark:border-slate-700">
                      <td className="px-2 py-1"><Input className="text-xs h-7" value={item.product_code} onChange={e => updateItem(idx, "product_code", e.target.value)} /></td>
                      <td className="px-2 py-1"><Input className="text-xs h-7" value={item.product_name} onChange={e => updateItem(idx, "product_name", e.target.value)} placeholder="Tên sản phẩm" /></td>
                      <td className="px-2 py-1"><Input type="number" className="text-xs h-7 text-right" value={item.quantity} onChange={e => updateItem(idx, "quantity", Number(e.target.value))} /></td>
                      <td className="px-2 py-1"><Input type="number" className="text-xs h-7 text-right" value={item.price} onChange={e => updateItem(idx, "price", Number(e.target.value))} /></td>
                      <td className="px-2 py-1"><Input type="number" className="text-xs h-7 text-right" value={item.import_price} onChange={e => updateItem(idx, "import_price", Number(e.target.value))} /></td>
                      <td className="px-2 py-1">
                        {items.length > 1 && <button onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Đóng</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-create-po">
            {submitting ? "Đang tạo..." : "Tạo phiếu nhập"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
